import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { ArrowDropDown, ArrowDropUp, FilterAlt, FilterAltOutlined } from "@mui/icons-material";
import { useAuthHeader } from "react-auth-kit";

import { formatDate } from "../../components/Reports/Components/utils";
import { useGlobalContext } from "../../context/global";
import useUserDetails from "../../helper/userData/useUserDetails";
import useInitialLoad from "../../hooks/useInitialLoad";
import useWrapperInsightsQuery from "../../hooks/useWrapperInsightsQuery";
import { mergeCustomOutputToAPIFiltersFormat } from "../../helper/Quadrant/quadrantHelper";
import {
  processFields,
} from "../../components/Filter/subComponents/filterHelper";

import Loader from "../../components/loader";
import SectionTitle from "../../components/sectionTitle";
import Breadcrumb from "../../components/Breadcrumb/ReportsBreadCrumb";
import ComplexFilterWidget from "../../components/Filter/ComplexFilterWidget";
import AppliedFiltersChips from "../../components/Filter/AppliedFiltersChips";
import ExportDownloadTooltip, {
  EXPORT_LIMIT_TOOLTIP_MESSAGE,
  EXPORT_TRACK_TOOLTIP_MESSAGE,
  getExportRedirectIconButtonSx,
} from "../../components/Reports/Components/ExportDownloadTooltip";
import BulkActionBarV2 from "../../components/Bars/BulkActionBarV2";
import BulkActionConfirmModalV2 from "../../components/modal/BulkActionConfirmModalV2";
import BulkAdjustBidModalV2 from "../../components/modal/BulkAdjustBidModalV2";
import { getCampaignListV2BudgetRules } from "./campaignListV2BudgetConfig";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import PageVersionSwitchButton from "../../components/Buttons/PageVersionSwitchButton";
import {
  ColumnCustomizationButton,
  COLUMN_CUSTOMIZATION_PAGE_IDS,
  V2_STANDARD_VIEW_OPTIONS,
  usePageColumnCustomization,
} from "../../components/ColumnCustomization";
import {
  QuickFilterButton,
  QUICK_FILTER_PAGE_IDS,
} from "../../components/QuickFilter";

import CampaignListV2Table from "./CampaignListV2Table";

import {
  BULK_SCOPE,
  applyBulkRowToggle,
  applyBulkSelectAllPage,
  applySelectAllMatching,
  clearBulkSelection,
  getRowBulkId,
  getSelectedRowIds,
  shouldShowSelectAllMatchingBanner,
} from "../../helper/bulkSelection/bulkSelectionModes";
import { buildBulkQuery } from "../../helper/bulkSelection/buildBulkQuery";
import {
  CAMPAIGN_BULK_FIELD,
  buildBulkCampaignPatchBody,
  dedupeRowsByCampaignId,
  getRequestedCountForBulk,
  getUniqueCampaignCount,
  normalizeBidStrategyLabel,
  submitBulkCampaignPatchUpdate,
} from "../../helper/bulkSelection/bulkCampaignUpdate";

import {
  FiltersDimensionDataCampaignListV2,
  FiltersMeasuresDataCampaignListV2,
  FiltersCampaignDataCampaignListV2,
  CAMPAIGN_LIST_V2_DEFAULT_DIMENSIONS,
  CAMPAIGN_LIST_V2_PAGE_MEASURES,
  CAMPAIGN_LIST_V2_COLUMNS,
  CAMPAIGN_LIST_V2_ALWAYS_MEASURES,
  CAMPAIGN_LIST_V2_GROUP_BY_OPTIONS,
  getCampaignListV2Columns,
} from "./campaignListV2Configs";

import {
  injectCampaignListSearchIntoFilters,
  normalizeCampaignListInsightRows,
  prepareWrapperFilters,
  buildCampaignInsightsQueryBody,
} from "./campaignListV2LocalFilters";

import {
  exportCampaignListV2Report,
  getCampaignListV2VisibleColumnKeys,
  buildCampaignListV2WrapperExportQueryBody,
} from "../../helper/PageWiseExportLogic/campaignListV2Export";
import { openRequestTrackingPage } from "../../helper/common/openRequestTrackingPage";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const REPORT_API = process.env.REACT_APP_REPORT_API;
const USER_PLATFORM = "amazon";
const EMPTY_TOPX_CONFIG = { operators: {}, fields: [] };
const CAMPAIGN_LIST_BULK_ID_KEYS = ["campaign_id", "ad_group_id"];
const CAMPAIGN_ONLY_BULK_ID_KEYS = ["campaign_id"];
const CAMPAIGN_BUDGET_BULK_ID_KEYS = ["campaign_id"];

const normalizeCampaignStatus = (status) => String(status ?? "").toUpperCase();

const mapStatusToAdjustmentType = (status) =>
  normalizeCampaignStatus(status) === "PAUSED" ? "PAUSE" : "ENABLE";

const CampaignListV2 = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const location = useLocation();
  const viewTYPE =
    new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";

  const authHeader = useAuthHeader();
  const token = authHeader();

  const { calendarDate, applyCalendar, clientId } = useGlobalContext();
  const [userDetails] = useUserDetails();
  const [initialLoad, setInitialLoad] = useInitialLoad();

  const [showComplexFilter, setShowComplexFilter] = useState(false);
  const [filterModuleFilters, setFilterModuleFilters] = useState({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [insightsMenuAnchor, setInsightsMenuAnchor] = useState(null);

  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [groupByFilter, setGroupByFilter] = useState(
    CAMPAIGN_LIST_V2_GROUP_BY_OPTIONS[0]?.value ?? "campaign"
  );
  const [queryDimensions, setQueryDimensions] = useState(
    CAMPAIGN_LIST_V2_DEFAULT_DIMENSIONS
  );
  const queryMeasure = CAMPAIGN_LIST_V2_PAGE_MEASURES;

  const [queryOrder, setQueryOrder] = useState([["spends", "desc"]]);
  const [queryTimeDimensions, setQueryTimeDimensions] = useState([
    {
      dimension: "date",
      dateRange: [
        formatDate(calendarDate[0].startDate),
        formatDate(calendarDate[0].endDate),
      ],
    },
  ]);

  const allColumns = useMemo(
    () => getCampaignListV2Columns(queryDimensions),
    [queryDimensions]
  );

  const {
    currentViewType,
    selectedMetricKeys,
    columnCollectionId,
    metricColumns,
    columnCollections,
    visibleTableColumns,
    isSaving: isColumnCustomizationSaving,
    syncFromViewType,
    handleApply: handleApplyColumnCustomization,
    handleSave: handleSaveColumnCustomization,
    handleSaveAs: handleSaveAsColumnCustomization,
    handleRenameView: handleRenameColumnView,
    handleDeleteView: handleDeleteColumnView,
  } = usePageColumnCustomization({
    pageId: COLUMN_CUSTOMIZATION_PAGE_IDS.CAMPAIGN_LIST_V2,
    allColumns,
    standardViewOptions: V2_STANDARD_VIEW_OPTIONS,
    initialViewType: viewTYPE,
    platform: USER_PLATFORM,
    seedWithAllMetrics: true,
  });

  useEffect(() => {
    const viewtype =
      new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
    syncFromViewType(viewtype);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("viewtype", currentViewType);
    const newSearch = queryParams.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (newSearch === currentSearch) return;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${newSearch}`
    );
  }, [currentViewType]);

  const bulkIdKeys = useMemo(
    () =>
      queryDimensions.includes("ad_group_id")
        ? CAMPAIGN_LIST_BULK_ID_KEYS
        : CAMPAIGN_ONLY_BULK_ID_KEYS,
    [queryDimensions]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(50);
  const {
    rows: dataSource,
    setRows: setDataSource,
    summaryData,
    totalCount,
    isLoading: isLoaderVisible,
    runQuery,
    resetResult,
  } = useWrapperInsightsQuery({
    errorMessage: "Failed to fetch campaigns",
  });

  const [isExportDownloading, setIsExportDownloading] = useState(false);
  const [isExportRedirectMode, setIsExportRedirectMode] = useState(false);

  const [bulkScope, setBulkScope] = useState(BULK_SCOPE.SELECTED);
  const [bulkExcludeIds, setBulkExcludeIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [isBulkAdjustModalOpen, setIsBulkAdjustModalOpen] = useState(false);
  const [pendingBulkOperation, setPendingBulkOperation] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [isBulkSuccess, setIsBulkSuccess] = useState(false);

  const filterWidgetConfig = useMemo(
    () => ({
      dimension: FiltersDimensionDataCampaignListV2[USER_PLATFORM],
      measure: FiltersMeasuresDataCampaignListV2[USER_PLATFORM],
      campaigns: FiltersCampaignDataCampaignListV2[USER_PLATFORM],
      topXConfig: EMPTY_TOPX_CONFIG,
    }),
    []
  );

  // Resolve static filter dropdown options (campaign name/portfolio are backend-search; others are static).
  useEffect(() => {
    const init = async () => {
      const dimensionConfig = FiltersDimensionDataCampaignListV2[USER_PLATFORM];
      const measureConfig = FiltersMeasuresDataCampaignListV2[USER_PLATFORM];

      if (dimensionConfig?.fields) {
        dimensionConfig.fields = await processFields(dimensionConfig.fields ?? [], clientId, []);
      }
      if (measureConfig?.fields) {
        measureConfig.fields = await processFields(measureConfig.fields ?? [], clientId, []);
      }
    };
    if (clientId) init();
  }, [clientId]);

  // Keep dateRange synced with global calendar.
  useEffect(() => {
    const startDate = formatDate(calendarDate[0].startDate);
    const endDate = formatDate(calendarDate[0].endDate);
    const apiStart = queryTimeDimensions[0]?.dateRange?.[0];
    const apiEnd = queryTimeDimensions[0]?.dateRange?.[1];
    if (startDate !== apiStart || endDate !== apiEnd) {
      setQueryTimeDimensions([
        {
          dimension: "date",
          dateRange: [startDate, endDate],
        },
      ]);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyCalendar]);

  useEffect(() => {
    setIsExportRedirectMode(false);
  }, [filterModuleFilters, queryTimeDimensions, queryOrder, searchValue, isFilterApplied]);

  const selectedCampaigns = useMemo(
    () => dataSource.filter((row) => row?.isChecked),
    [dataSource]
  );

  const selectedBulkIds = useMemo(
    () => getSelectedRowIds(dataSource, bulkIdKeys),
    [dataSource, bulkIdKeys]
  );

  const pageSelectableCount = dataSource.length;
  const showSelectAllMatching = shouldShowSelectAllMatchingBanner({
    scope: bulkScope,
    pageSelectedCount: selectedCampaigns.length,
    pageSelectableCount,
    totalCount,
  });

  const clearEditingAndSelection = useCallback((rows) => {
    return (rows || []).map((r) => ({
      ...r,
      isChecked: false,
    }));
  }, []);

  const resetBulkSelectionState = () => {
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
    setDataSource((prev) => clearEditingAndSelection(prev));
  };

  // Clear selection on query changes.
  useEffect(() => {
    resetBulkSelectionState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginationLimit, filterModuleFilters, searchValue, queryOrder, queryTimeDimensions, queryDimensions]);

  function getSearchMembers() {
    return queryDimensions.includes("ad_group_name")
      ? ["campaign_name", "portfolio_name", "targeting", "ad_group_name"]
      : ["campaign_name", "portfolio_name", "targeting"];
  }

  const handleDataFetch = (moduleFilters) => {
    if (!clientId) return;

    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      moduleFilters?.metrics?.custom_output,
      moduleFilters?.dimension?.custom_output,
      moduleFilters?.campaigns?.custom_output,
      moduleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters, isEmptyMatch } =
      prepareWrapperFilters(mergedFilters);

    if (isEmptyMatch) {
      resetResult();
      setInitialLoad(false);
      return;
    }

    const filtersWithSearch = injectCampaignListSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSearchMembers()
    );

    runQuery(async ({ signal }) => {
      const queryBody = buildCampaignInsightsQueryBody({
        queryDimensions,
        filters: filtersWithSearch,
        queryMeasure,
        queryOrder,
        timeDimensions: queryTimeDimensions,
        offset: (currentPage - 1) * paginationLimit,
        limit: paginationLimit,
      });

      const summaryQueryBody = {
        query: {
          ...queryBody.query,
        },
      };

      const insightsUrl = `${REPORT_API}/client/${clientId}/adset:insights?source=${USER_PLATFORM}`;
      const summaryUrl = `${REPORT_API}/client/${clientId}/adset:insights-summary?source=${USER_PLATFORM}`;

      const [insightsRes, summaryRes] = await Promise.all([
        axios.post(insightsUrl, queryBody, { signal }),
        axios.post(summaryUrl, summaryQueryBody, { signal }),
      ]);

      const summary = summaryRes?.data ?? {};
      return {
        rows: normalizeCampaignListInsightRows(insightsRes?.data?.data ?? []),
        summaryData: summary,
        totalCount: Number(summary?.total_count) || 0,
      };
    }).finally(() => {
      setInitialLoad(false);
    });
  };

  useEffect(() => {
    handleDataFetch(filterModuleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterModuleFilters,
    queryTimeDimensions,
    queryOrder,
    currentPage,
    paginationLimit,
    searchValue,
    searchTrigger,
    clientId,
    queryDimensions,
  ]);

  const handleFilterOutput = (filters) => {
    const nextFilters = filters || {};
    setFilterModuleFilters(nextFilters);
    setIsFilterApplied(
      (nextFilters?.dimension?.data?.length ?? 0) > 0 ||
        (nextFilters?.metrics?.data?.length ?? 0) > 0
    );
    setShowComplexFilter(false);
    setCurrentPage(1);
  };

  const FilterComponent = (
    <ComplexFilterWidget
      key={`campaign_list_v2_${showComplexFilter}`}
      config={filterWidgetConfig}
      campaignFilter={false}
      showDimensionFilter={true}
      filters={filterModuleFilters}
      showPopupModal={showComplexFilter}
      setShowPopupModal={setShowComplexFilter}
      output={handleFilterOutput}
      prefixMember={""}
      type="page"
      sidebar={true}
      default={"metrics"}
      page={"overlay"}
      isAppliedCallback={() => {}}
      summary={"always"}
      summaryOutput={() => {}}
      calloutTitle="Select your filters"
      calloutContent="+ Add Filter"
      hideFilterSummary={true}
    />
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / paginationLimit));
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePageLimitChange = (event) => {
    setPaginationLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  const singleCheckBoxHandler = (index) => {
    setDataSource((prev) => {
      const result = applyBulkRowToggle({
        rows: prev,
        index,
        scope: bulkScope,
        excludeIds: bulkExcludeIds,
        idKeys: bulkIdKeys,
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const allCheckBoxHandler = (checked) => {
    setDataSource((prev) => {
      const result = applyBulkSelectAllPage({ rows: prev, checked });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const removeAllSelected = () => {
    setDataSource((prev) => clearBulkSelection(prev).rows);
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
  };

  const handleSelectAllMatching = () => {
    setDataSource((prev) => {
      const result = applySelectAllMatching({ rows: prev });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const getCurrentBulkFilters = () => {
    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      filterModuleFilters?.metrics?.custom_output,
      filterModuleFilters?.dimension?.custom_output,
      filterModuleFilters?.campaigns?.custom_output,
      filterModuleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters } = prepareWrapperFilters(mergedFilters);
    return injectCampaignListSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSearchMembers()
    );
  };

  const prepareBulkQuery = ({
    scopeOverride,
    selectedRowsOverride,
    excludeRowsOverride,
    idKeys = CAMPAIGN_BUDGET_BULK_ID_KEYS,
  } = {}) => {
    const scope = scopeOverride || bulkScope;
    const selectedRows =
      selectedRowsOverride !== undefined
        ? selectedRowsOverride
        : dedupeRowsByCampaignId(selectedCampaigns);
    const excludeRows =
      excludeRowsOverride !== undefined
        ? excludeRowsOverride
        : dedupeRowsByCampaignId(
            dataSource.filter((row) =>
              bulkExcludeIds.includes(getRowBulkId(row, bulkIdKeys))
            )
          );

    return buildBulkQuery({
      scope,
      baseFilters: getCurrentBulkFilters(),
      selectedRows,
      excludeRows,
      idKeys,
      queryContext: {
        dimensions: queryDimensions,
        measures: [...queryMeasure, ...CAMPAIGN_LIST_V2_ALWAYS_MEASURES],
        order: queryOrder,
        timeDimensions: queryTimeDimensions,
      },
    });
  };

  const handleToggleStatus = (row) => {
    if (!row?.campaign_id || isBulkSubmitting) return;
    const currentStatus = normalizeCampaignStatus(row?.campaign_status);
    const nextStatus = currentStatus === "ENABLED" ? "PAUSED" : "ENABLED";

    setIsBulkSuccess(false);
    setPendingBulkOperation({
      type: "status",
      status: nextStatus,
      rows: [row],
      scope: BULK_SCOPE.SELECTED,
    });
    setBulkConfirm({
      actionKey: nextStatus === "PAUSED" ? "Pause" : "Enable",
      title:
        nextStatus === "PAUSED"
          ? "Pause this campaign?"
          : "Enable this campaign?",
      actionLabel: nextStatus === "PAUSED" ? "Pause" : "Enable",
      description: null,
    });
  };

  const handleBidStrategyChange = (rowIndex, nextStrategyLabel) => {
    const row = dataSource[rowIndex];
    if (!row?.campaign_id || isBulkSubmitting) return;

    const normalizedStatus = normalizeCampaignStatus(row?.status);
    if (normalizedStatus === "ARCHIVED") return;

    const nextLabel = normalizeBidStrategyLabel(nextStrategyLabel);
    const prevLabel = normalizeBidStrategyLabel(
      row.bid_strategy_api_value ?? row.campaign_bid_strategy
    );
    if (prevLabel === nextLabel) return;

    setDataSource((prev) =>
      prev.map((r, i) => {
        if (i !== rowIndex) return r;
        return {
          ...r,
          bid_strategy_api_value: nextLabel,
          campaign_bid_strategy: nextLabel,
        };
      })
    );

    setIsBulkSuccess(false);
    setPendingBulkOperation({
      type: "bidStrategy",
      campaignId: row.campaign_id,
      prevStrategy: prevLabel,
      strategy: nextLabel,
      rows: [row],
      scope: BULK_SCOPE.SELECTED,
    });
    setBulkConfirm({
      actionKey: "Bid Strategy",
      title: "Confirm bid strategy change?",
      actionLabel: "Confirm",
      description: (
        <>
          Set bid strategy to{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {nextLabel}
          </Box>
          .
        </>
      ),
    });
  };

  const handleSelectionBarClick = (optionKey) => {
    if (isBulkSubmitting) return;

    if (optionKey === "Adjust Budget") {
      setPendingBulkOperation(null);
      setIsBulkSuccess(false);
      setIsBulkAdjustModalOpen(true);
      return;
    }

    const status = optionKey === "Pause" ? "PAUSED" : "ENABLED";
    setIsBulkSuccess(false);
    setPendingBulkOperation({ type: "status", status });
    setBulkConfirm({
      actionKey: optionKey,
      title:
        optionKey === "Pause"
          ? "Pause selected campaigns?"
          : "Enable selected campaigns?",
      actionLabel: optionKey,
      description: null,
    });
  };

  const handleBulkAdjustModalSubmit = (operation) => {
    setPendingBulkOperation({
      type: "budget",
      ...operation,
    });
    const valueText = operation?.adjustmentType?.includes("PERCENTAGE")
      ? `${operation.value}%`
      : String(operation.value);
    setBulkConfirm({
      actionKey: "Adjust Budget",
      title: "Confirm bulk budget adjustment?",
      actionLabel: "Confirm",
      description: (
        <>
          Apply{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {operation.label}
          </Box>{" "}
          (
          <Box component="span" sx={{ fontWeight: 700 }}>
            {valueText}
          </Box>
          ) to the selected campaigns.
        </>
      ),
    });
  };

  const handleBulkConfirm = async () => {
    if (!bulkConfirm || !pendingBulkOperation || isBulkSubmitting) {
      setBulkConfirm(null);
      return;
    }

    const email = userDetails?.user?.email ?? localStorage.getItem("email") ?? "";
    const isBidStrategy = pendingBulkOperation.type === "bidStrategy";
    const isBudget = pendingBulkOperation.type === "budget";
    const isStatus = pendingBulkOperation.type === "status";
    const operationRows = pendingBulkOperation.rows;
    const isSingleRowStatus = isStatus && Boolean(operationRows?.length);
    const operationScope = pendingBulkOperation.scope || bulkScope;

    let requestedCount;
    let bulkPayload;
    let operation;

    if (isBidStrategy) {
      requestedCount = 1;
      bulkPayload = prepareBulkQuery({
        scopeOverride: BULK_SCOPE.SELECTED,
        selectedRowsOverride: [{ campaign_id: pendingBulkOperation.campaignId }],
        excludeRowsOverride: [],
        idKeys: CAMPAIGN_BUDGET_BULK_ID_KEYS,
      });
      operation = {
        field: CAMPAIGN_BULK_FIELD.BID_STRATEGY,
        strategy: pendingBulkOperation.strategy,
      };
    } else if (isBudget || isStatus) {
      const uniqueSelected = isSingleRowStatus
        ? dedupeRowsByCampaignId(operationRows)
        : dedupeRowsByCampaignId(selectedCampaigns);
      const uniqueExcluded = isSingleRowStatus
        ? []
        : dedupeRowsByCampaignId(
            dataSource.filter((row) =>
              bulkExcludeIds.includes(getRowBulkId(row, bulkIdKeys))
            )
          );

      requestedCount = isSingleRowStatus
        ? uniqueSelected.length
        : operationScope === BULK_SCOPE.SELECTED
          ? getUniqueCampaignCount(selectedCampaigns)
          : getRequestedCountForBulk({
              scope: operationScope,
              selectedIdsCount: uniqueSelected.length,
              totalCount,
              excludeIdsCount: uniqueExcluded.length,
            });

      if (requestedCount <= 0) {
        toast.info(
          isStatus
            ? "No campaigns selected for status update."
            : "No campaigns selected for budget update."
        );
        setBulkConfirm(null);
        setPendingBulkOperation(null);
        return;
      }

      bulkPayload = prepareBulkQuery({
        scopeOverride: operationScope,
        selectedRowsOverride: uniqueSelected,
        excludeRowsOverride: uniqueExcluded,
        idKeys: CAMPAIGN_BUDGET_BULK_ID_KEYS,
      });

      operation = isStatus
        ? {
            field: CAMPAIGN_BULK_FIELD.STATUS,
            adjustmentType: mapStatusToAdjustmentType(pendingBulkOperation.status),
          }
        : {
            field: CAMPAIGN_BULK_FIELD.DAILY_BUDGET,
            adjustmentType: pendingBulkOperation.adjustmentType,
            value: Number(pendingBulkOperation.value),
          };
    } else {
      setBulkConfirm(null);
      setPendingBulkOperation(null);
      return;
    }

    const body = buildBulkCampaignPatchBody({
      query: bulkPayload?.query,
      email,
      requestedCount,
      operation,
    });

    setBulkConfirm(null);
    setIsBulkAdjustModalOpen(false);
    setIsBulkSubmitting(true);
    setInitialLoad(true);

    try {
      await submitBulkCampaignPatchUpdate({
        clientId,
        userPlatform: USER_PLATFORM,
        skipEvents: true,
        body,
        token,
      });
      if (isBudget || (isStatus && !isSingleRowStatus)) {
        removeAllSelected();
      }
      setPendingBulkOperation(null);
      setIsBulkSuccess(true);
      setBulkConfirm({
        actionKey: isBidStrategy
          ? "Bid Strategy"
          : isStatus
            ? "Status"
            : "Adjust Budget",
        title: "Request Submitted",
        actionLabel: "Close",
        description: null,
      });
    } catch (error) {
      console.error(error);
      if (isBidStrategy) {
        setDataSource((prev) =>
          prev.map((r) =>
            r.campaign_id === pendingBulkOperation.campaignId
              ? {
                  ...r,
                  bid_strategy_api_value: pendingBulkOperation.prevStrategy,
                  campaign_bid_strategy: pendingBulkOperation.prevStrategy,
                }
              : r
          )
        );
      }
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to submit bulk campaign update."
      );
      setBulkConfirm(null);
      setPendingBulkOperation(null);
    } finally {
      setIsBulkSubmitting(false);
      setInitialLoad(false);
    }
  };

  const isBudgetPercentageDisabled =
    bulkScope === BULK_SCOPE.SELECTED &&
    dedupeRowsByCampaignId(selectedCampaigns).some(
      (row) =>
        row?.campaign_budget === null ||
        row?.campaign_budget === undefined ||
        row?.campaign_budget === ""
    );

  const handleExportClick = async () => {
    if (isExportRedirectMode) {
      openRequestTrackingPage();
      return;
    }
    const exportTotalCount = totalCount || dataSource?.length || 0;
    if (exportTotalCount <= 0) {
      toast.info("No records available for export.");
      return;
    }

    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      filterModuleFilters?.metrics?.custom_output,
      filterModuleFilters?.dimension?.custom_output,
      filterModuleFilters?.campaigns?.custom_output,
      filterModuleFilters?.topX?.custom_output
    );

    const { filters: transformedFilters, isEmptyMatch } = prepareWrapperFilters(mergedFilters);
    if (isEmptyMatch) {
      toast.info("No records available for export.");
      return;
    }

    const filtersWithSearch = injectCampaignListSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSearchMembers()
    );

    const insightsQueryBody = buildCampaignInsightsQueryBody({
      queryDimensions,
      filters: filtersWithSearch,
      queryMeasure,
      queryOrder,
      timeDimensions: queryTimeDimensions,
    });
    delete insightsQueryBody.query.offset;
    delete insightsQueryBody.query.limit;

    await exportCampaignListV2Report({
      clientId,
      userPlatform: USER_PLATFORM,
      triggeredBy: userDetails?.user?.email ?? localStorage.getItem("email") ?? "",
      totalCount: exportTotalCount,
      queryBody: buildCampaignListV2WrapperExportQueryBody({ query: insightsQueryBody.query }),
      visibleColumnKeys: getCampaignListV2VisibleColumnKeys(visibleTableColumns),
      setIsExporting: setIsExportDownloading,
      onExportSuccess: () => setIsExportRedirectMode(true),
    });
  };

  const campaignBudgetRules = useMemo(() => getCampaignListV2BudgetRules(), []);

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box-campaign-list-v2"
    >
      <div className="campaign-list-container overflow-hidden">
        <Box>
          <Breadcrumb />
          <Stack direction="row" alignItems="center">
            <SectionTitle sectionTitleHeading="Campaigns" />
            <PageVersionSwitchButton pageKey="campaign-list" isNewVersion />
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              mt: 1,
              position: "relative",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              options={CAMPAIGN_LIST_V2_GROUP_BY_OPTIONS}
              disableClearable
              getOptionLabel={(option) => option?.label || ""}
              value={
                CAMPAIGN_LIST_V2_GROUP_BY_OPTIONS.find(
                  (option) => option.value === groupByFilter
                ) || CAMPAIGN_LIST_V2_GROUP_BY_OPTIONS[0]
              }
              onChange={(_, selectedOption) => {
                if (!selectedOption) return;
                setGroupByFilter(selectedOption.value);
                setQueryDimensions([...(selectedOption.dimensions || [])]);
                setCurrentPage(1);
              }}
              size="small"
              sx={{
                minWidth: "200px",
                "& .MuiAutocomplete-inputRoot": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiAutocomplete-input": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiSvgIcon-root": {
                  color: "#333333",
                },
              }}
              slotProps={{
                listbox: {
                  sx: {
                    maxHeight: 250,
                    overflow: "auto",
                    fontSize: "12px",
                    "& .MuiAutocomplete-option": {
                      fontSize: "12px",
                      color: "#333333",
                      padding: "6px 12px",
                      "&:hover": { backgroundColor: "#EDEDED" },
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Analysis by"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      color: "#333333",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#333333",
                    },
                    "& .MuiInputBase-root": {
                      height: "36px",
                      fontSize: "12px !important",
                      color: "#333333",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "12px !important",
                      color: "#333333",
                      padding: "6px 8px",
                    },
                  }}
                />
              )}
            />

            <TextField
              size="small"
              placeholder="Search Campaign Name, Portfolio…"
              variant="outlined"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  setSearchValue(e.target.value);
                  setSearchTrigger((prev) => prev + 1);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "16px", color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchValue("");
                        setInputValue("");
                        setCurrentPage(1);
                      }}
                      edge="end"
                    >
                      <CloseIcon sx={{ fontSize: "16px", color: "#333333" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flexGrow: 1,
                minWidth: 200,
                "& .MuiInputBase-root": {
                  height: "36px",
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  color: "#333333",
                  padding: "6px 4px",
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "12px",
                  color: "#000000",
                  opacity: 0.6,
                },
              }}
            />

            <ColumnCustomizationButton
              collections={columnCollections}
              columns={metricColumns}
              activeCollectionId={columnCollectionId}
              selectedColumnKeys={selectedMetricKeys}
              onApply={handleApplyColumnCustomization}
              onSave={handleSaveColumnCustomization}
              onSaveAs={handleSaveAsColumnCustomization}
              onRenameView={handleRenameColumnView}
              onDeleteView={handleDeleteColumnView}
              saving={isColumnCustomizationSaving}
            />

            <QuickFilterButton
              clientId={clientId}
              platform={USER_PLATFORM}
              pageId={QUICK_FILTER_PAGE_IDS.CAMPAIGN_LIST_V2}
              filterConfig={filterWidgetConfig}
              campaignFilter={false}
              onApply={handleFilterOutput}
            />

            <ToggleButton
              value="filters"
              selected={showComplexFilter}
              onChange={() => setShowComplexFilter(!showComplexFilter)}
              sx={{
                height: "36px",
                borderRadius: 1,
                px: 1.5,
                borderColor: showComplexFilter ? "grey.700" : "grey.400",
                backgroundColor: showComplexFilter ? "grey.100" : "white",
                color: "#333333",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: showComplexFilter ? "grey.200" : "grey.50",
                },
                "& .MuiTypography-root": { fontSize: "12px", color: "#333333" },
                "& svg": { color: "#333333" },
              }}
            >
              <Badge
                overlap="circular"
                variant="dot"
                invisible={!isFilterApplied}
                sx={{ "& .MuiBadge-badge": { bgcolor: isFilterApplied ? "primary.main" : "transparent" } }}
              >
                {showComplexFilter ? <FilterAlt fontSize="small" /> : <FilterAltOutlined fontSize="small" />}
              </Badge>
              <Typography fontWeight={500} sx={{ ml: 1 }}>Filters</Typography>
              {showComplexFilter ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
            </ToggleButton>

            <ExportDownloadTooltip
              autoOpen={isExportRedirectMode}
              title={
                isExportRedirectMode
                  ? EXPORT_TRACK_TOOLTIP_MESSAGE
                  : EXPORT_LIMIT_TOOLTIP_MESSAGE
              }
            >
              <span>
                <IconButton
                  onClick={handleExportClick}
                  disabled={isExportDownloading}
                  sx={getExportRedirectIconButtonSx(isExportRedirectMode)}
                >
                  {isExportDownloading ? (
                    <CircularProgress size={18} thickness={5} sx={{ color: "grey.600" }} />
                  ) : isExportRedirectMode ? (
                    <OpenInNew sx={{ fontSize: 20 }} />
                  ) : (
                    <FileDownloadOutlined sx={{ fontSize: 22 }} />
                  )}
                </IconButton>
              </span>
            </ExportDownloadTooltip>

            <Button
              variant="outlined"
              size="small"
              startIcon={<InsightsOutlined fontSize="small" />}
              onClick={(event) => setInsightsMenuAnchor(event.currentTarget)}
              sx={{
                height: "36px",
                textTransform: "none",
                fontSize: "12px",
                color: "#333333",
                borderColor: "grey.400",
                whiteSpace: "nowrap",
                "&:hover": { borderColor: "grey.700", backgroundColor: "grey.50" },
              }}
            >
              View more Insights
            </Button>

            <Menu
              anchorEl={insightsMenuAnchor}
              open={Boolean(insightsMenuAnchor)}
              onClose={() => setInsightsMenuAnchor(null)}
              disableScrollLock
            >
              {[
                { label: "Portfolio Report", href: "/portfolio-report" },
                { label: "Campaign Report", href: "/campaign-report" },
                { label: "Campaign Format Report", href: "/campaign-format-report" },
                { label: "Adgroup Report", href: "/ad-group-report" },
              ].map((item) => (
                <MenuItem
                  key={item.href}
                  sx={{ fontSize: "12px" }}
                  onClick={() => {
                    window.open(item.href, "_blank", "noopener,noreferrer");
                    setInsightsMenuAnchor(null);
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {showComplexFilter && FilterComponent}
          <AppliedFiltersChips
            filters={filterModuleFilters}
            config={filterWidgetConfig}
            onChange={handleFilterOutput}
            hideSaveActions
          />

          <BulkActionBarV2
            open={
              selectedCampaigns.length > 0 ||
              bulkScope === BULK_SCOPE.ALL_MATCHING ||
              bulkScope === BULK_SCOPE.ALL_MATCHING_EXCEPT
            }
            scope={bulkScope}
            selectedIdsCount={selectedBulkIds.length}
            totalCount={totalCount}
            excludeIdsCount={bulkExcludeIds.length}
            showSelectAllMatching={showSelectAllMatching}
            onSelectAllMatching={handleSelectAllMatching}
            onClear={removeAllSelected}
            actions={[
              {
                key: "Adjust Budget",
                label: isBulkSubmitting ? "Submitting..." : "Adjust Budget",
                variant: "outlined",
              },
              { key: "Pause", label: "Pause", variant: "outlined" },
              { key: "Enable", label: "Enable", variant: "contained", color: "primary" },
            ]}
            onAction={handleSelectionBarClick}
          />

          <BulkAdjustBidModalV2
            open={isBulkAdjustModalOpen}
            title="Adjust Campaign Budget"
            subtitle="Choose how you want to update daily budget for the selected campaigns."
            previewRows={
              bulkScope === BULK_SCOPE.SELECTED
                ? dedupeRowsByCampaignId(selectedCampaigns)
                : []
            }
            isPercentageFieldsDisabled={isBudgetPercentageDisabled}
            previewLabelKey="campaign_name"
            previewSecondaryKey="campaign_id"
            previewValueKey="campaign_budget"
            bidRules={campaignBudgetRules}
            valueNoun="budget"
            onClose={() => {
              if (isBulkSubmitting) return;
              setIsBulkAdjustModalOpen(false);
              setPendingBulkOperation(null);
            }}
            onSubmit={handleBulkAdjustModalSubmit}
          />

          <BulkActionConfirmModalV2
            open={Boolean(bulkConfirm)}
            title={bulkConfirm?.title}
            actionLabel={
              isBulkSubmitting
                ? "Submitting..."
                : bulkConfirm?.actionLabel || "Confirm"
            }
            description={bulkConfirm?.description}
            scope={
              pendingBulkOperation?.type === "bidStrategy" ||
              pendingBulkOperation?.rows?.length
                ? BULK_SCOPE.SELECTED
                : bulkScope
            }
            selectedIdsCount={
              pendingBulkOperation?.type === "bidStrategy"
                ? 1
                : pendingBulkOperation?.rows?.length
                  ? getUniqueCampaignCount(pendingBulkOperation.rows)
                  : (pendingBulkOperation?.type === "budget" ||
                        pendingBulkOperation?.type === "status") &&
                      bulkScope === BULK_SCOPE.SELECTED
                    ? getUniqueCampaignCount(selectedCampaigns)
                    : selectedBulkIds.length
            }
            totalCount={totalCount}
            excludeIdsCount={
              pendingBulkOperation?.type === "bidStrategy" ||
              pendingBulkOperation?.rows?.length
                ? 0
                : bulkExcludeIds.length
            }
            zIndex={1500}
            isSuccess={isBulkSuccess}
            successTitle="Request Submitted"
            successMessage="Your bulk action request has been submitted successfully. You can track it on the Request Tracking page."
            onGoToTracking={openRequestTrackingPage}
            onCancel={() => {
              if (isBulkSubmitting) return;
              if (
                pendingBulkOperation?.type === "bidStrategy" &&
                pendingBulkOperation?.prevStrategy
              ) {
                setDataSource((prev) =>
                  prev.map((r) =>
                    r.campaign_id === pendingBulkOperation.campaignId
                      ? {
                          ...r,
                          bid_strategy_api_value: pendingBulkOperation.prevStrategy,
                          campaign_bid_strategy: pendingBulkOperation.prevStrategy,
                        }
                      : r
                  )
                );
              }
              setBulkConfirm(null);
              setPendingBulkOperation(null);
              setIsBulkSuccess(false);
            }}
            onConfirm={handleBulkConfirm}
          />

          <CampaignListV2Table
            columns={visibleTableColumns}
            rows={dataSource}
            isLoading={isLoaderVisible || initialLoad}
            queryOrder={queryOrder}
            summaryData={summaryData}
            onSortClick={(key, order) => {
              setCurrentPage(1);
              setQueryOrder([[key, order]]);
            }}
            onToggleRow={singleCheckBoxHandler}
            onToggleAll={allCheckBoxHandler}
            onToggleStatus={handleToggleStatus}
            onChangeBidStrategy={handleBidStrategyChange}
          />

          {!isLoaderVisible &&
            !initialLoad &&
            (totalCount > 0 || dataSource?.length > 0) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#4f5366" }}>
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>Page Size</Typography>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={paginationLimit}
                      onChange={handlePageLimitChange}
                      disabled={isLoaderVisible}
                      sx={{ height: 36, fontSize: "12px", color: "#333333" }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option} sx={{ fontSize: "12px" }}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  lazyLoading={isLoaderVisible}
                  totalItems={totalCount}
                  itemsPerPage={paginationLimit}
                />
              </Box>
            )}
        </Box>
      </div>
      {initialLoad ? <Loader /> : null}
    </div>
  );
};

export default CampaignListV2;

